// 3rd party libs
import {
    mapGetters,
    mapActions,
} from 'vuex';

const LIMITS_TYPE = {
    TRANSFER_OUT: 'Transfer out',
    TOPUP: 'Acquire token',
};

// Shared
import sharedEpicTypes from '@shared/epics/types.js';

const kycMixin = {
    data() {
        return {
            level: 0,
            currentLevel: {
                topUpAndReceiveLimit: 0,
                sendLimit: 0,
            },
            nextLevel: {
                topUpAndReceiveLimit: 0,
                sendLimit: 0,
            },
        };
    },
    computed: {
        ...mapGetters([
            'fiatCurrencyIndex',
            'preferredFiatCurrency',
            'kycTierLevel',
            'getCumulativeLimitById',
            'fiatCurrenciesList',
            'defaultCurrency',
            'getCurrencyShortCodeFormatted',
        ]),

        ...mapGetters('wallet', ['isChzAllowed']),

        cumulativeLimits() {
            return this.getCumulativeLimitById(this.defaultCurrency?.id);
        },
    },
    watch: {
        cumulativeLimits(newValue) {
            // If currency chz is updated, then get updated KYC limits
            if (newValue.length > 0) {
                this.setLevels(newValue);
            }
        },
    },
    async created() {
        const convertAmountToCurrencies = this.isChzAllowed
            ? [this.preferredFiatCurrency.id, this.defaultCurrency.id]
            : [this.defaultCurrency.id];

        await this.getCumulativeLimits({
            currencyIds: [this.defaultCurrency.id],
            convertAmountToCurrencies,
        });
    },
    methods: {
        ...mapActions({
            getCumulativeLimits: sharedEpicTypes.GET_CUMULATIVE_LIMITS,
        }),

        kycMixinSetLevel(level) {
            this.level = level;
            this.setLevels(this.cumulativeLimits);
        },

        setLevels(limits) {
            this.currentLevel = this.getUpdatedLevels(limits).currentLevel;
            this.nextLevel = this.getUpdatedLevels(limits).nextLevel;
        },

        /**
         * Returns the limits for the current and next levels based on the cumulative limits
         *
         * @param {Array} cumulativeLimits Cumulative limits array
         * @returns {object} updated limits
         */
        getUpdatedLevels(cumulativeLimits) {
            let currentLevel = 0,
                nextLevel = 0;

            if (cumulativeLimits && cumulativeLimits.length) {
                currentLevel = this.buildLevel(cumulativeLimits);
                nextLevel = this.buildLevel(cumulativeLimits, 1);
            }

            return {
                currentLevel,
                nextLevel,
            };
        },

        /**
         * Build particular KYC object based on requested level
         *
         * @param {Array} cumulativeLimits Cumulative limits array
         * @param {number} addLevels If provided, data from current level + added value will be requested
         * @returns {object} requested KYC level info
         */
        buildLevel(cumulativeLimits, addLevels = 0) {
            const KYCLevel = this.level + addLevels;
            const topUpAndReceiveLevel = cumulativeLimits.find(({
                cumulativeLimitType = {},
                ...others
            } = {}) => others.kycTierLevel === KYCLevel && cumulativeLimitType.name === LIMITS_TYPE.TOPUP) || {};

            const sendLevel = cumulativeLimits.find(({
                cumulativeLimitType = {},
                ...others
            } = {}) => others.kycTierLevel === KYCLevel && cumulativeLimitType.name === LIMITS_TYPE.TRANSFER_OUT) || {};

            let sendLimit = 0,
                sendLimitFiat = 0,
                sendLimitFiatCurrency = '',
                topUpAndReceiveLimit = 0,
                topUpAndReceiveLimitFiat = 0,
                topUpAndReceiveLimitFiatCurrency = '';

            // Use Math.floor since limits can have decimals
            if (topUpAndReceiveLevel.convertedPrices &&
                topUpAndReceiveLevel.convertedPrices[this.defaultCurrency?.id] &&
                topUpAndReceiveLevel.convertedPrices[this.defaultCurrency?.id].expressedCurrencyLimit
            ) {
                topUpAndReceiveLimit = Math.floor(topUpAndReceiveLevel.convertedPrices[this.defaultCurrency.id].expressedCurrencyLimit || 0);
            }

            const foundFiatCurrencyIndex = this.fiatCurrencyIndex(this.preferredFiatCurrency.id, 'id');

            if (topUpAndReceiveLevel.convertedPrices &&
                topUpAndReceiveLevel.convertedPrices[this.preferredFiatCurrency.id] &&
                topUpAndReceiveLevel.convertedPrices[this.preferredFiatCurrency.id].expressedCurrencyLimit
            ) {
                topUpAndReceiveLimitFiat = Math.floor(topUpAndReceiveLevel.convertedPrices[this.preferredFiatCurrency.id].expressedCurrencyLimit || 0);
                topUpAndReceiveLimitFiatCurrency = foundFiatCurrencyIndex > -1 && this.fiatCurrenciesList.length > 0
                    ? this.fiatCurrenciesList[foundFiatCurrencyIndex].shortCode
                    : '';
            }

            if (sendLevel.convertedPrices &&
                sendLevel.convertedPrices[this.defaultCurrency.id] &&
                sendLevel.convertedPrices[this.defaultCurrency.id].expressedCurrencyLimit
            ) {
                sendLimit = Math.floor(sendLevel.convertedPrices[this.defaultCurrency.id].expressedCurrencyLimit || 0);
            }

            if (sendLevel.convertedPrices &&
                sendLevel.convertedPrices[this.preferredFiatCurrency.id] &&
                sendLevel.convertedPrices[this.preferredFiatCurrency.id].expressedCurrencyLimit
            ) {
                sendLimitFiat = Math.floor(sendLevel.convertedPrices[this.preferredFiatCurrency.id].expressedCurrencyLimit || 0);
                sendLimitFiatCurrency = foundFiatCurrencyIndex > -1 && this.fiatCurrenciesList.length > 0
                    ? this.fiatCurrenciesList[foundFiatCurrencyIndex].shortCode
                    : '';
            }

            return {
                topUpAndReceiveLimit,
                topUpAndReceiveLimitFiat,
                topUpAndReceiveLimitFiatCurrency,
                sendLimit,
                sendLimitFiat,
                sendLimitFiatCurrency,
            };
        },
    },
};

export default kycMixin;